import React, { FC, ReactElement, useEffect } from "react";
import { useTranslation } from "react-i18next";

import MDButton from "components/MDButton";
import NegativeImage from "assets/images/NegativeImage.svg?react";
import PositiveImage from "assets/images/PositiveImage.svg?react";

import { makeStyles } from "tss-react/mui";
import { useTheme } from "@mui/material/styles";
import { styles } from "./TestResults.styles";
import { ResultActionType } from "types/resultActions";
import { useAppSelector } from "store/hooks";
import { getTestTypeValue } from "store/slices/test";
import { getTestTypeLocaleKey } from "utils/testType.utils";
import { sentryLogMessage } from "services/sentryLogger";
import { SENTRY_EVENT_TYPES } from "constants/sentryEvents";
import { RESULT_STATUS_MAP } from "types/imageRecognition";
import { getModelMetaDataTags } from "services/models";

interface StepProps {
  handleUpdateStep: (step: number) => void;
  setResultAction: (action: ResultActionType) => void;
  activeInnerStep?: number;
}

interface ResultComponentProps {
  classes: Record<string, string>,
  image: React.ReactNode,
  title: string;
  handleUpdateStep: (step: number) => void;
  setResultAction: (action: ResultActionType) => void;
}

type Step1Map = {
  [key: number]: {
    Component: ReactElement;
  }
};

const ResultComponent: FC<ResultComponentProps> = ({
  classes,
  image,
  title,
  handleUpdateStep,
  setResultAction,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const testType = useAppSelector(getTestTypeValue);
  const testTypeLocaleKey = getTestTypeLocaleKey(testType);
  return (
    <div className={classes.resultComponentContainer}>
      <div className={classes.resultBody}>
        <div className={classes.image}>{image}</div>
        <div className={classes.title}>{title}</div>
        <div className={classes.text}>{t(`${testTypeLocaleKey}:ResultScreen:resultText`)}</div>
      </div>
      <div className={classes.buttonsSection}>
        <div
          className={classes.link}
          onClick={() => {
            handleUpdateStep(6);
            setResultAction("REPORT");
          }}
        >
          {t(`${testTypeLocaleKey}:ResultScreen:reportLink`)}
        </div>
        <MDButton
          style={{ width: theme.width.full, height: theme.spacing.spacing28 }}
          color={"primary"}
          variant="contained"
          type="submit"
          onClick={() => {
            handleUpdateStep(6);
            setResultAction("DONE");
          }}
        >
          {t(`${testTypeLocaleKey}:ResultScreen:buttons:doneButton`)}
        </MDButton>
      </div>
    </div>
  );
};

const TestResults: FC<StepProps> = ({ handleUpdateStep, setResultAction, activeInnerStep }) => {
  const { t } = useTranslation();
  const testType = useAppSelector(getTestTypeValue);
  const testTypeLocaleKey = getTestTypeLocaleKey(testType);
  const theme = useTheme();
  const useStyles = makeStyles()(() => styles(theme));
  const { classes } = useStyles();
  // const result = useAppSelector((state) => state.imageRecognition.result);

  // Log 'Test completed' when result screen is displayed
  useEffect(() => {
    if (activeInnerStep && (activeInnerStep === 1 || activeInnerStep === 2 || activeInnerStep === 3)) {
      sentryLogMessage({
        message: 'Test completed',
        level: 'info',
        tags: {
          component: 'TestResults',
          category: 'test_completed',
          event_type: SENTRY_EVENT_TYPES.COMPLETED_TEST,
          ...getModelMetaDataTags(),
        },
        extra: {
          result: RESULT_STATUS_MAP[activeInnerStep],
          activeInnerStep,
          timestamp: new Date().toISOString(),
        },
      });
    }
  }, [activeInnerStep]);

  const body: Step1Map = {
    1: {
      Component: (
        <ResultComponent
          classes={classes}
          image={<PositiveImage />}
          title={t(`${testTypeLocaleKey}:ResultScreen:resultTitlePositive`)}
          handleUpdateStep={handleUpdateStep}
          setResultAction={setResultAction}
        />
      ),
    },
    2: {
      Component: (
        <ResultComponent
          classes={classes}
          image={<NegativeImage />}
          title={t(`${testTypeLocaleKey}:ResultScreen:resultTitleNegative`)}
          handleUpdateStep={handleUpdateStep}
          setResultAction={setResultAction}
        />
      ),
    },
    3: {
      Component: (
        <ResultComponent
          classes={classes}
          image={<NegativeImage />}
          title={t(`${testTypeLocaleKey}:ResultScreen:resultTitleUnvalid`)}
          handleUpdateStep={handleUpdateStep}
          setResultAction={setResultAction}
        />
      ),
    }
  };

  return <div className={classes.container}>{body[activeInnerStep].Component}</div>;
};

export default TestResults;
